import {
  cleanCustomerDetails,
  customerDetailsChanges,
  customerDetailsProblems,
  phoneKey,
  type CustomerDetailField,
  type CustomerDetails,
  type CustomerDetailsHistoryEntry,
  type CustomerDetailsInput,
  type CustomerDetailsProblem,
} from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { updateCustomerDetailsIntent, type UpdateCustomerDetailsResult } from '@/api/customerDetailsIntents';
import { intentFailureCopy } from '@/api/intents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { flushWritesAndWait } from '@/state/writeQueue';
import { actorLabel, currentActorId } from '@/domain/actor';
import type { CustomerRecord } from '@/domain/types';

export type SaveCustomerDetailsResult =
  | { ok: true; unchanged: boolean }
  | { ok: false; problems: CustomerDetailsProblem[] }
  | { ok: false; refused: { field?: CustomerDetailField; message: string } };

/** A record's editable details as the pop-up shows them ('' = none). */
export function customerDetailsOf(c: CustomerRecord): CustomerDetails {
  return {
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    preferredName: c.preferredName ?? '',
    phone: c.phone ?? '',
    email: c.email ?? '',
    instagramHandle: c.instagramHandle ?? '',
    suburb: c.suburb ?? '',
    state: c.state ?? '',
    postcode: c.postcode ?? '',
  };
}

const OPTIONAL: ReadonlySet<CustomerDetailField> = new Set(['preferredName', 'email', 'instagramHandle']);

/** The record with the changed fields applied (an optional field cleared is dropped). */
function withDetails(c: CustomerRecord, next: CustomerDetails, fields: CustomerDetailField[]): CustomerRecord {
  const out: Record<string, unknown> = { ...c };
  for (const f of fields) {
    if (OPTIONAL.has(f) && !next[f]) delete out[f];
    else out[f] = next[f];
  }
  return out as unknown as CustomerRecord;
}

/** The other customer already holding this mobile (the pop-up's warning), if any. */
export function customerWithMobile(
  customers: Record<string, CustomerRecord>,
  phone: string,
  exceptId: string,
): CustomerRecord | undefined {
  const key = phoneKey(phone);
  if (!key) return undefined;
  return Object.values(customers).find((c) => c.id !== exceptId && phoneKey(c.phone) === key);
}

/**
 * Staff save a customer's edited details from the record (tv-pzvy, approved 6 Oct).
 *
 * Checks the same rules as the server first (`@atink/domain` customerDetails). The
 * served build mirrors the change at once without a bridge PUT (the server's ONE
 * intent writes the columns and the history line) and resolves once the server
 * answers: a refusal restores the record and names the field. Store-fallback (no
 * server) applies it locally.
 */
export async function saveCustomerDetails(
  customerId: string,
  input: CustomerDetailsInput,
  /** What the pop-up opened with: only the fields edited against it are saved, so a
   *  field the user never touched can't write back an old value (another seat's edit). */
  baseline?: CustomerDetails,
): Promise<SaveCustomerDetailsResult> {
  const customer = useBrainStore.getState().customers[customerId];
  if (!customer) return { ok: false, refused: { message: 'This customer is not on file.' } };
  const onFile = customerDetailsOf(customer);
  let edit: CustomerDetailsInput = input;
  if (baseline) {
    const typed = cleanCustomerDetails(input, baseline);
    edit = Object.fromEntries(customerDetailsChanges(baseline, typed).map((f) => [f, typed[f]]));
  }
  const next = cleanCustomerDetails(edit, onFile);
  const problems = customerDetailsProblems(next, onFile);
  if (problems.length) return { ok: false, problems };
  const fields = customerDetailsChanges(onFile, next);
  if (fields.length === 0) return { ok: true, unchanged: true };

  const nowIso = useSimulationStore.getState().clockIso;
  const by = currentActorId();
  const was: CustomerDetailsHistoryEntry['was'] = {};
  const oldName = `${onFile.firstName} ${onFile.lastName}`.trim();
  if ((fields.includes('firstName') || fields.includes('lastName')) && oldName) was.name = oldName;
  if (fields.includes('phone') && onFile.phone) was.mobile = onFile.phone;
  if (fields.includes('email') && onFile.email) was.email = onFile.email;
  const localEntry: CustomerDetailsHistoryEntry = {
    id: `act-details-${customerId}-${new Date(nowIso).getTime()}-${Math.random().toString(36).slice(2, 10)}`,
    at: nowIso,
    ...(by ? { by, byName: actorLabel(by) } : {}),
    fields,
    was,
  };
  const mirrored: CustomerRecord = {
    ...withDetails(customer, next, fields),
    detailsHistory: [...(customer.detailsHistory ?? []), localEntry],
  };

  if (isStoreFallbackEnabled()) {
    useBrainStore.getState().upsertCustomer(mirrored);
    return { ok: true, unchanged: false };
  }

  const pairs = [['customers', customerId]] as const;
  // Touch only what this save owns on the CURRENT record: anything a doorbell
  // brought in meanwhile (a new message, another seat's change) stays.
  const patchCurrent = (fn: (cur: CustomerRecord) => CustomerRecord) =>
    withServerAuthoredWrites(pairs, () => {
      const cur = useBrainStore.getState().customers[customerId];
      if (cur) useBrainStore.getState().upsertCustomer(fn(cur));
    });
  const withoutLocal = (cur: CustomerRecord) => (cur.detailsHistory ?? []).filter((h) => h.id !== localEntry.id);
  withServerAuthoredWrites(pairs, () => useBrainStore.getState().upsertCustomer(mirrored));

  const sent: CustomerDetailsInput = {};
  for (const f of fields) sent[f] = next[f];
  // A customer PUT still queued (or retrying) carries the old details: land it
  // first, so it can't arrive after the intent and undo it (tv-2na.8.11 pattern).
  const res: UpdateCustomerDetailsResult = await flushWritesAndWait()
    .catch(() => undefined)
    .then(() => updateCustomerDetailsIntent({ customerId, ...sent, nowIso }))
    .catch((): UpdateCustomerDetailsResult => ({ ok: false, kind: 'transient', status: 0, error: 'network' }));
  if (res.ok) {
    // The server's stamp (its clock, its actor) replaces the local line; a save the
    // server found already applied (another seat) leaves no line of ours.
    const entry = res.entry;
    patchCurrent((cur) => {
      const rest = withoutLocal(cur);
      return { ...cur, detailsHistory: entry && !rest.some((h) => h.id === entry.id) ? [...rest, entry] : rest };
    });
    return { ok: true, unchanged: res.unchanged };
  }

  // Refused: put back only the fields this save changed, and drop its line.
  patchCurrent((cur) => {
    const restored: Record<string, unknown> = { ...cur };
    for (const f of fields) {
      if (customer[f] === undefined) delete restored[f];
      else restored[f] = customer[f];
    }
    return { ...(restored as unknown as CustomerRecord), detailsHistory: withoutLocal(cur) };
  });
  const retry = intentFailureCopy(res).retry;
  const message = retry
    ? "We couldn't reach the server, so nothing changed. Try again."
    : `The server refused it: ${(res.message ?? res.code ?? res.error).replace(/\.+$/, '')}. Nothing changed.`;
  useUIStore.getState().pushNotification({
    intensity: 'critical',
    title: "Couldn't save the details",
    body: message,
    ttlMs: 10000,
    customerId,
  });
  const field = res.field;
  return { ok: false, refused: { ...(field ? { field } : {}), message: res.message ?? message } };
}
